import { useExportAction } from "@/action/use-export-action";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { UserAvatar } from "@/components/display/UserAvatar";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useAssignUsersToKit,
  useGroupAssignmentsData,
  useUnassignUsersFromKit,
} from "@/data/use-assignments-data";
import { useGroupExpertsData } from "@/data/use-group-experts-data";
import { useKitsData } from "@/data/use-kits-data";
import { useDeleteScript, useScriptsData } from "@/data/use-scripts-data";
import { useSettingData } from "@/data/use-settings-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import { Kit } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faDownload,
  faEllipsisV,
  faPlus,
  faTrash,
  faUserPlus,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCreateInterviewAction } from "../../action/use-create-interview-action";
import { Checkbox } from "../../components/ui/checkbox";

export function InterviewsAdminPage() {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const { value: interviewerFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_INTERVIEWER",
  );
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("interviews");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  useEffect(() => {
    if (interviewerFeatureFlag !== "enabled") {
      setIsCreateDialogOpen(false);
    }
  }, [interviewerFeatureFlag]);
  const [interviewName, setInterviewName] = useState("");
  const [interviewTopic, setInterviewTopic] = useState("");
  const [selectedExpertUuid, setSelectedExpertUuid] = useState<string>("");
  const [selectedScriptUuid, setSelectedScriptUuid] = useState<string>("");
  const [selectedUserUuids, setSelectedUserUuids] = useState<string[]>([]);
  const [kitUuidToDelete, setKitUuidToDelete] = useState<string | null>(null);

  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [managingKit, setManagingKit] = useState<Kit | null>(null);
  const [manageSelectedUserUuids, setManageSelectedUserUuids] = useState<
    string[]
  >([]);

  const [loadingOverlay, setLoadingOverlay] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const { create: createInterview, isCreatingInterview } =
    useCreateInterviewAction();
  const { assignUsers, isAssigning } = useAssignUsersToKit(groupUuid);
  const { unassignUsers, isUnassigning } = useUnassignUsersFromKit(groupUuid);

  const {
    kits: interviews,
    isLoading: isLoadingInterviews,
    createKit,
  } = useKitsData(groupUuid, { type: "interview" });
  const { users, isLoading: isLoadingUsers } = useUsersData(groupUuid);
  const { experts, isLoading: isLoadingExperts } =
    useGroupExpertsData(groupUuid);
  const { assignments, isLoading: isLoadingAssignments } =
    useGroupAssignmentsData(groupUuid);
  const { scripts, isLoading: isLoadingScripts } = useScriptsData(groupUuid);

  const { deleteScript, isDeleting: isDeletingScript } =
    useDeleteScript(groupUuid);

  const { exportKitBundle } = useExportAction();

  // Filter for interviewer experts
  const interviewerExperts = experts.filter(
    (expert) => expert.expertType === "interviewer",
  );

  const groupedInterviews = groupByDate(interviews, "date_updated");

  const columns: GroupedTableColumn<Kit>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_assignments", label: "Assigned To" },
  ];

  async function handleCreateInterview() {
    if (!interviewName.trim() || !selectedExpertUuid) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // If no users selected, create a single interview with no assignments
      if (selectedUserUuids.length === 0) {
        await createInterview({
          expertUuid: selectedExpertUuid,
          name: interviewName.trim(),
          scriptUuid: selectedScriptUuid || undefined,
          description: interviewTopic.trim() || undefined,
        });

        // Reset form
        setInterviewName("");
        setInterviewTopic("");
        setSelectedExpertUuid("");
        setSelectedScriptUuid("");
        setSelectedUserUuids([]);
        setIsCreateDialogOpen(false);

        toast.success("Interview created successfully");
        return;
      }

      // Create separate interviews for each selected user in parallel
      const results = await Promise.allSettled(
        selectedUserUuids.map(async (userUuid) => {
          const { kitUuid } = await createInterview({
            expertUuid: selectedExpertUuid,
            name: interviewName.trim(),
            scriptUuid: selectedScriptUuid || undefined,
            description: interviewTopic.trim() || undefined,
          });

          await assignUsers({
            kitUuid,
            userUuids: [userUuid],
          });

          return { userUuid, kitUuid };
        }),
      );

      // Count successes and failures
      const successes = results.filter((r) => r.status === "fulfilled");
      const failures = results.filter((r) => r.status === "rejected");

      // Log failures for debugging
      if (failures.length > 0) {
        failures.forEach((failure, index) => {
          console.error(
            `Failed to create interview ${index + 1}:`,
            "reason" in failure ? failure.reason : null,
          );
        });
      }

      // Reset form
      setInterviewName("");
      setInterviewTopic("");
      setSelectedExpertUuid("");
      setSelectedScriptUuid("");
      setSelectedUserUuids([]);
      setIsCreateDialogOpen(false);

      // Show appropriate toast message
      if (failures.length === 0) {
        toast.success(
          `${successes.length} interview${successes.length !== 1 ? "s" : ""} created successfully`,
        );
      } else if (successes.length > 0) {
        toast.warning(
          `${successes.length} of ${results.length} interviews created. ${failures.length} failed.`,
        );
      } else {
        toast.error("Failed to create interviews");
      }
    } catch (error) {
      console.error("Failed to create interview:", error);
      toast.error("Failed to create interviews");
    }
  }

  function toggleUserSelection(userUuid: string) {
    setSelectedUserUuids((prev) =>
      prev.includes(userUuid)
        ? prev.filter((id) => id !== userUuid)
        : [...prev, userUuid],
    );
  }

  function openManageDialog(kit: Kit) {
    setManagingKit(kit);
    const currentAssignments =
      kit.user_assignments?.map((a) => a.user_uuid) || [];
    setManageSelectedUserUuids(currentAssignments);
    setIsManageDialogOpen(true);
  }

  function toggleManageUserSelection(userUuid: string) {
    setManageSelectedUserUuids((prev) =>
      prev.includes(userUuid)
        ? prev.filter((id) => id !== userUuid)
        : [...prev, userUuid],
    );
  }

  async function handleSaveAssignments() {
    if (!managingKit) return;

    try {
      const currentAssignments =
        managingKit.user_assignments?.map((a) => a.user_uuid) || [];
      const toAssign = manageSelectedUserUuids.filter(
        (uuid) => !currentAssignments.includes(uuid),
      );
      const toUnassign = currentAssignments.filter(
        (uuid) => !manageSelectedUserUuids.includes(uuid),
      );

      // Assign new users
      if (toAssign.length > 0) {
        await assignUsers({
          kitUuid: managingKit.uuid,
          userUuids: toAssign,
        });
      }

      // Unassign removed users
      if (toUnassign.length > 0) {
        await unassignUsers({
          kitUuid: managingKit.uuid,
          userUuids: toUnassign,
        });
      }

      setIsManageDialogOpen(false);
      setManagingKit(null);
      setManageSelectedUserUuids([]);
      toast.success("Assignments updated successfully");
    } catch (error) {
      console.error("Failed to update assignments:", error);
      toast.error("Failed to update assignments");
    }
  }

  // Script handlers
  async function handleDeleteScript(scriptUuid: string) {
    try {
      await deleteScript(scriptUuid);
    } catch (error) {
      console.error("Failed to delete script:", error);
    }
  }

  const handleDownloadBundleClick = async (kit: Kit) => {
    if (!groupUuid) return;
    try {
      await exportKitBundle(
        `Interview - ${kit.name}`,
        { format: "html", includeFiles: true },
        kit.uuid,
      );
    } catch {
      toast.error("Failed to export kit documents");
    }
  };

  const handleCreateProject = async (kit: Kit) => {
    setLoadingOverlay({
      title: `Creating Workbook`,
      description: `This might take a few seconds.`,
    });
    const newKit = await createKit({
      name: `Workbook from ${kit.name} Interview`,
      type: "project",
      presetKitUuid: kit.uuid,
    });
    if (!newKit) {
      toast.error("Failed to create workbook from interview");
      return;
    }
    setLoadingOverlay(null);
    navigate({
      to: "/w/$groupSlug/k/$kitUuid",
      params: { groupSlug, kitUuid: newKit.uuid },
    });
  };

  const handleCreateExpert = async (kit: Kit) => {
    if (!groupUuid) return;

    setLoadingOverlay({
      title: `Creating Expert`,
      description: `This might take a few seconds.`,
    });
    const newKit = await createKit({
      name: `Expert from ${kit.name} Interview`,
      type: "expert",
      presetKitUuid: kit.uuid,
    });
    if (!newKit) {
      toast.error("Failed to create expert from interview");
      return;
    }
    setLoadingOverlay(null);
    navigate({
      to: "/w/$groupSlug/k/$kitUuid",
      params: { groupSlug, kitUuid: newKit.uuid },
    });
  };

  const renderCheckedActions = (selectedItems: Map<string, Kit>) => {
    const kits = Array.from(selectedItems.values());

    return (
      <Tooltip>
        <TooltipContent>Download {kits.length} bundles</TooltipContent>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={async () => {
              for (const kit of kits) {
                await handleDownloadBundleClick(kit);
              }
            }}
            aria-label={`Download ${kits.length} bundles`}
          >
            <FontAwesomeIcon icon={faDownload} />
          </Button>
        </TooltipTrigger>
      </Tooltip>
    );
  };

  return (
    <>
      <Page>
        <PageHeader>
          <PageHeader.Title>Interviews Admin</PageHeader.Title>
        </PageHeader>
        <PageBody>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList>
              <TabsTrigger value="interviews">Interviews</TabsTrigger>
              <TabsTrigger value="scripts">Scripts</TabsTrigger>
              <TabsTrigger value="assignments">All Assignments</TabsTrigger>
            </TabsList>

            <TabsContent value="interviews" className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Interview Kits</h3>
                  <p className="text-muted-foreground text-sm">
                    Create interview kits and assign them to users
                  </p>
                </div>
                <Dialog
                  open={
                    interviewerFeatureFlag === "enabled" && isCreateDialogOpen
                  }
                  onOpenChange={setIsCreateDialogOpen}
                >
                  {interviewerFeatureFlag === "enabled" && (
                    <DialogTrigger asChild>
                      <Button>Create Interview</Button>
                    </DialogTrigger>
                  )}
                  <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Interview Kit</DialogTitle>
                      <DialogDescription>
                        Create a new interview kit and assign it to users
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Interview Name *</Label>
                        <Input
                          id="name"
                          placeholder="e.g., Q4 Performance Review"
                          value={interviewName}
                          onChange={(e) => setInterviewName(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="topic">Topic</Label>
                        <Textarea
                          id="topic"
                          placeholder="Describe what this interview is about..."
                          value={interviewTopic}
                          onChange={(e) => setInterviewTopic(e.target.value)}
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="expert">Interviewer Expert *</Label>
                        <Select
                          value={selectedExpertUuid}
                          onValueChange={setSelectedExpertUuid}
                        >
                          <SelectTrigger id="expert">
                            <SelectValue placeholder="Select an interviewer expert" />
                          </SelectTrigger>
                          <SelectContent>
                            {isLoadingExperts ? (
                              <SelectItem value="loading" disabled>
                                Loading experts...
                              </SelectItem>
                            ) : interviewerExperts.length === 0 ? (
                              <SelectItem value="none" disabled>
                                No interviewer experts found
                              </SelectItem>
                            ) : (
                              interviewerExperts.map((expert) => (
                                <SelectItem
                                  key={expert.uuid}
                                  value={expert.uuid}
                                >
                                  {expert.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="script">
                          Interview Script (Optional)
                        </Label>
                        <Select
                          value={selectedScriptUuid}
                          onValueChange={(value) => {
                            setSelectedScriptUuid(
                              value === "none" ? "" : value,
                            );
                          }}
                        >
                          <SelectTrigger id="script">
                            <SelectValue placeholder="No script selected" />
                          </SelectTrigger>
                          <SelectContent>
                            {isLoadingScripts ? (
                              <SelectItem value="loading" disabled>
                                Loading scripts...
                              </SelectItem>
                            ) : scripts.length === 0 ? (
                              <SelectItem value="none" disabled>
                                No scripts found
                              </SelectItem>
                            ) : (
                              scripts.map((script) => (
                                <SelectItem
                                  key={script.uuid}
                                  value={script.uuid}
                                >
                                  {script.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-muted-foreground text-xs">
                          Scripts are optional. Leave unselected to skip.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Create Interview For (Optional)</Label>
                        <p className="text-muted-foreground text-sm">
                          Select users to create individual interviews for
                        </p>
                        <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border p-4">
                          {isLoadingUsers ? (
                            <p className="text-muted-foreground text-sm">
                              Loading users...
                            </p>
                          ) : users.length === 0 ? (
                            <p className="text-muted-foreground text-sm">
                              No users found
                            </p>
                          ) : (
                            users.map((user) => (
                              <div
                                key={user.uuid}
                                className="hover:bg-accent flex items-center space-x-2 rounded p-2"
                              >
                                <Checkbox
                                  id={`user-${user.uuid}`}
                                  checked={selectedUserUuids.includes(
                                    user.uuid,
                                  )}
                                  onCheckedChange={() =>
                                    toggleUserSelection(user.uuid)
                                  }
                                />
                                <label
                                  htmlFor={`user-${user.uuid}`}
                                  className="flex flex-1 cursor-pointer items-center space-x-2"
                                >
                                  <UserAvatar user={user} size="sm" />
                                  <span className="text-sm">{user.name}</span>
                                  <span className="text-muted-foreground text-xs">
                                    {user.email}
                                  </span>
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateInterview}
                        disabled={
                          isCreatingInterview ||
                          isAssigning ||
                          !interviewName.trim() ||
                          !selectedExpertUuid
                        }
                      >
                        {isCreatingInterview || isAssigning
                          ? "Creating..."
                          : "Create Interview"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {isLoadingInterviews ? (
                <div className="text-muted-foreground rounded-lg border p-8 text-center">
                  Loading interviews...
                </div>
              ) : interviews.length === 0 ? (
                <div className="text-muted-foreground rounded-lg border p-8 text-center">
                  No interview kits yet. Create one to get started.
                </div>
              ) : (
                <GroupedTable
                  data={groupedInterviews}
                  columns={columns}
                  checkedActions={renderCheckedActions}
                  rowLink={(row) => ({
                    to: "/w/$groupSlug/settings/interviews/$kitUuid",
                    params: { groupSlug, kitUuid: row.uuid },
                  })}
                  renderCell={{
                    name: (_value, kit) => <KitDisplayName kit={kit} />,
                    date_updated: (_value, kit) => smartDate(kit.date_updated),
                    user_assignments: (_value, kit) => {
                      const assignments = kit.user_assignments || [];
                      if (assignments.length === 0) {
                        return (
                          <span className="text-muted-foreground text-sm">
                            No assignments
                          </span>
                        );
                      }
                      return (
                        <div className="flex items-center gap-2">
                          {assignments.map((assignment) => (
                            <div key={assignment.user_uuid}>
                              {assignment.user ? (
                                <UserDisplayName user={assignment.user} />
                              ) : (
                                <span
                                  key={assignment.uuid}
                                  className="bg-muted rounded px-2 py-1 text-xs"
                                >
                                  {assignment.user_uuid}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    },
                  }}
                  renderActions={({ row: kit }) => (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="More actions"
                            title="More actions"
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleCreateProject(kit)}
                          >
                            <FontAwesomeIcon icon={faPlus} /> Create Workbook
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCreateExpert(kit)}
                          >
                            <FontAwesomeIcon icon={faPlus} /> Create Expert
                          </DropdownMenuItem>

                          <Separator />

                          <DropdownMenuItem asChild>
                            <Link
                              to="/w/$groupSlug/settings/interviews/$kitUuid"
                              params={{ groupSlug, kitUuid: kit.uuid }}
                              className="flex flex-row items-center gap-2"
                            >
                              <FontAwesomeIcon icon={faComment} /> View
                              Transcripts
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDownloadBundleClick(kit)}
                          >
                            <FontAwesomeIcon icon={faDownload} /> Download
                            Transcripts
                          </DropdownMenuItem>
                          <Separator />

                          <DropdownMenuItem
                            onClick={() => openManageDialog(kit)}
                          >
                            <FontAwesomeIcon icon={faUserPlus} /> Assign Users
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setKitUuidToDelete(kit.uuid)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                />
              )}
            </TabsContent>

            <TabsContent value="scripts" className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Interview Scripts</h3>
                  <p className="text-muted-foreground text-sm">
                    Create and manage interview scripts
                  </p>
                </div>
                <Button
                  onClick={() =>
                    navigate({
                      to: "/w/$groupSlug/settings/script/new",
                      params: { groupSlug },
                    })
                  }
                >
                  Create Script
                </Button>
              </div>

              {isLoadingScripts ? (
                <div className="text-muted-foreground rounded-lg border p-8 text-center">
                  Loading scripts...
                </div>
              ) : scripts.length === 0 ? (
                <div className="text-muted-foreground rounded-lg border p-8 text-center">
                  No scripts yet. Create one to get started.
                </div>
              ) : (
                <div className="rounded-lg border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Description
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Last Modified
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {scripts.map((script) => (
                          <tr
                            key={script.uuid}
                            className="hover:bg-muted/30 border-b last:border-0"
                          >
                            <td className="px-4 py-3 font-medium">
                              {script.name}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {script.description || (
                                <span className="text-muted-foreground italic">
                                  No description
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {smartDate(script.dateUpdated)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate({
                                      to: "/w/$groupSlug/settings/script/$scriptUuid",
                                      params: {
                                        groupSlug,
                                        scriptUuid: script.uuid,
                                      },
                                    })
                                  }
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteScript(script.uuid)
                                  }
                                  disabled={isDeletingScript}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-2"
                                  />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="assignments" className="mt-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold">All Assignments</h3>
                <p className="text-muted-foreground text-sm">
                  View and manage all kit assignments
                </p>
              </div>

              {isLoadingAssignments ? (
                <div className="text-muted-foreground rounded-lg border p-8 text-center">
                  Loading assignments...
                </div>
              ) : assignments.length > 0 ? (
                <div className="rounded-lg border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            User
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Kit
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Assigned Date
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map((assignment) => {
                          const kit = interviews.find(
                            (k) => k.uuid === assignment.kit_uuid,
                          );
                          const user = users.find(
                            (u) => u.uuid === assignment.user_uuid,
                          );
                          return (
                            <tr
                              key={assignment.uuid}
                              className="hover:bg-muted/30 border-b last:border-0"
                            >
                              <td className="px-4 py-3">
                                {user ? (
                                  <div className="flex items-center space-x-3">
                                    <UserAvatar user={user} size="sm" />
                                    <div>
                                      <p className="text-sm font-medium">
                                        {user.name}
                                      </p>
                                      <p className="text-muted-foreground text-xs">
                                        {user.email}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-sm">
                                    Unknown user
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {kit ? (
                                  <KitDisplayName kit={kit} />
                                ) : (
                                  <span className="text-muted-foreground text-sm">
                                    Unknown kit
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {smartDate(assignment.date_created)}
                              </td>
                              <td className="px-4 py-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await unassignUsers({
                                        kitUuid: assignment.kit_uuid,
                                        userUuids: [assignment.user_uuid],
                                      });
                                      toast.success("Assignment removed");
                                    } catch (error) {
                                      console.error(
                                        "Failed to remove assignment:",
                                        error,
                                      );
                                      toast.error(
                                        "Failed to remove assignment",
                                      );
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-2"
                                  />
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground rounded-lg border p-8 text-center">
                  No assignments yet
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PageBody>
      </Page>

      {/* Manage Assignments Dialog */}
      <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Assignments</DialogTitle>
            <DialogDescription>
              Update user assignments for {managingKit?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Assign Users</Label>
              <p className="text-muted-foreground text-sm">
                Select users to assign this interview to
              </p>
              <div className="max-h-[400px] space-y-2 overflow-y-auto rounded-md border p-4">
                {isLoadingUsers ? (
                  <p className="text-muted-foreground text-sm">
                    Loading users...
                  </p>
                ) : users.length > 0 ? (
                  users.map((user) => (
                    <div
                      key={user.uuid}
                      className="hover:bg-muted flex items-center space-x-3 rounded-md p-2"
                    >
                      <Checkbox
                        id={`manage-user-${user.uuid}`}
                        checked={manageSelectedUserUuids.includes(user.uuid)}
                        onCheckedChange={() =>
                          toggleManageUserSelection(user.uuid)
                        }
                      />
                      <label
                        htmlFor={`manage-user-${user.uuid}`}
                        className="flex flex-1 cursor-pointer items-center space-x-3"
                      >
                        <UserAvatar user={user} size="md" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{user.name}</p>
                          <p className="text-muted-foreground text-xs">
                            {user.email}
                          </p>
                        </div>
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No users available
                  </p>
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                {manageSelectedUserUuids.length} user
                {manageSelectedUserUuids.length !== 1 ? "s" : ""} selected
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsManageDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignments}
              disabled={isAssigning || isUnassigning}
            >
              {isAssigning || isUnassigning ? "Saving..." : "Save Assignments"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {groupUuid && kitUuidToDelete && (
        <KitDeletionDialog
          groupUuid={groupUuid}
          kitUuid={kitUuidToDelete}
          open
          onOpenChange={() => setKitUuidToDelete(null)}
        />
      )}

      <LoadingOverlay
        loading={loadingOverlay !== null}
        title={loadingOverlay?.title}
        description={loadingOverlay?.description}
      />
    </>
  );
}
